/**
 * The **This run** tab: one run's header, its head-to-head, its ranked targets, and
 * what it ruled out.
 *
 * The section order is load-bearing and it is the same order as before, with the band
 * inserted at the top:
 *
 * 1. **`RunHeader`** — which run, whether it is still going, and *which criteria and
 *    which graph it scored against*. Everything below is only comparable to another
 *    run through that provenance line.
 * 2. **`LeaderBand`** — rank 1 against rank 2 of this run. The one place a versus
 *    framing is honest, because these two played each other in one tournament; the
 *    cumulative board deliberately has no equivalent.
 * 3. **`TargetTable`** — the ranking, and the answer to "which protein do I take into
 *    a screening programme".
 * 4. **`DeadEndTable`** — *below* the ranking because what review ruled out is the
 *    run's working, and a sweep that ranked nothing is exactly when that list is the
 *    result.
 * 5. **`KgContradictionTable`** — beneath those, because a disputed graph value is a
 *    statement about the graph rather than about a hypothesis and can sit on a target
 *    that ranked first or on one that was disqualified.
 *
 * Takes the whole `useLeaderboard` result for the same reason `BoardTab` does: this
 * component is the tab, and `App.tsx` is frozen.
 */
import { DeadEndTable } from './DeadEndTable'
import { KgContradictionTable } from './KgContradictionTable'
import { LeaderBand } from './LeaderBand'
import { Pagination } from './Pagination'
import { RunHeader } from './RunHeader'
import { TargetTable } from './TargetTable'
import { Card } from './ui/Card'
import { EmptyState, LoadingSkeleton } from './ui/States'
import type { ReportRow } from '../api/types'
import type { useLeaderboard } from '../hooks/useLeaderboard'
import type { ReportsState } from '../hooks/useReports'

interface Props {
  board: ReturnType<typeof useLeaderboard>
  reports: ReportsState
  accountEmail: string
  /**
   * The report a `?report=` link pointed at, once its run's rows have loaded.
   * Resolved in `App.tsx` because the link is a page-level concern read once at
   * module scope, and rendered here because it belongs beside the run it names.
   */
  linkedReport: ReportRow | undefined
}

export function RunTab({ board, reports, accountEmail, linkedReport }: Props) {
  if (!board.run) {
    // Three states, and the difference matters more here than anywhere else on the
    // page: "still loading", "no run has been selected" and "the request failed" led
    // to one grey sentence that read as the second in all three cases — so a broken
    // API looked like an idle harness.
    if (board.loading) return <LoadingSkeleton rows={8} />
    // `App.tsx` already renders `FailedState` with a retry above the tab body, so
    // rendering nothing here is what keeps a failed fetch from *also* claiming that
    // no run exists. Two contradictory statements about one request is worse than one.
    if (board.error) return null
    return (
      <Card>
        <EmptyState
          message="No run has been ranked yet."
          hint="Pick a run from the rail, or start a sweep with target-discovery run --autonomous. A finished sweep appears here as soon as the report-generator writes its ranking."
        />
      </Card>
    )
  }

  return (
    <>
      <RunHeader
        run={board.run}
        targetCount={board.total}
        onStop={board.stopRun}
        stopping={board.stopping === board.run.run_id}
        stopNotice={board.stopNotice}
      />
      <LeaderBand targets={board.targets} />
      {linkedReport && (
        <div className="rounded-xl border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm text-slate-200">
          {linkedReport.downloadable ? (
            <>
              The report you were emailed for{' '}
              <span className="font-medium text-slate-100">
                {linkedReport.protein ?? 'this target'}
              </span>{' '}
              is ready.{' '}
              <button
                type="button"
                onClick={() => void reports.download(linkedReport)}
                disabled={reports.downloading === linkedReport.id}
                className="font-medium text-brand-300 underline hover:text-brand-200 disabled:text-slate-500"
              >
                {reports.downloading === linkedReport.id
                  ? 'Downloading…'
                  : 'Download it'}
              </button>
              .
            </>
          ) : (
            <>
              The report for{' '}
              <span className="font-medium text-slate-100">
                {linkedReport.protein ?? 'this target'}
              </span>{' '}
              is {linkedReport.status}
              {/* Distinct from "not found": a document expires out of the bucket
                  after 90 days while the row stays, and the copy attached to the
                  email is still the document. */}
              {linkedReport.status === 'sent'
                ? ' — the stored copy has expired, but the PDF attached to your email is the same document.'
                : '. This page updates itself while it is being written.'}
            </>
          )}
        </div>
      )}
      <TargetTable
        targets={board.targets}
        sort={board.sort}
        direction={board.direction}
        onSort={board.toggleSort}
        reports={reports}
        accountEmail={accountEmail}
      />
      <Pagination
        total={board.total}
        limit={board.limit}
        offset={board.offset}
        onOffsetChange={board.setOffset}
        onPageSizeChange={board.setPageSize}
        noun="targets"
        disabled={board.loading}
      />
      <DeadEndTable
        deadEnds={board.deadEnds}
        total={board.deadEndTotal}
        counts={board.deadEndCounts}
        final={board.deadEndFinal}
        loaded={board.deadEndsLoaded}
      />
      <KgContradictionTable
        contradictions={board.kgContradictions}
        total={board.kgContradictionTotal}
        loaded={board.kgContradictionsLoaded}
      />
    </>
  )
}
